import axios from 'axios'
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type { RequestConfig, RequestInterceptors } from './types'
import { showMessage } from './status'
import { toast } from 'vue-sonner'

/**
 * 封装后的请求类
 * 1. 统一的错误处理
 * 2. 支持请求/响应拦截器
 * 3. 类型安全的响应处理
 */
class Request {
  // Axios 实例
  instance: AxiosInstance
  // 拦截器对象
  interceptorsObj?: RequestInterceptors<AxiosResponse>

  constructor(config: RequestConfig) {
    this.instance = axios.create(config)
    this.interceptorsObj = config.interceptors

    // --------------------------------------------------------------------------------
    // 1. 全局请求拦截器
    // --------------------------------------------------------------------------------
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // TODO: 在此处添加全局逻辑，例如在 Header 中添加 token
        // const token = localStorage.getItem('token')
        // if (token) {
        //   config.headers.Authorization = `Bearer ${token}`
        // }
        return config
      },
      (err: any) => err,
    )

    // --------------------------------------------------------------------------------
    // 2. 实例级别的请求拦截器 (来自配置)
    // --------------------------------------------------------------------------------
    this.instance.interceptors.request.use(
      this.interceptorsObj?.requestInterceptors,
      this.interceptorsObj?.requestInterceptorsCatch,
    )

    // --------------------------------------------------------------------------------
    // 3. 实例级别的响应拦截器 (来自配置)
    // --------------------------------------------------------------------------------
    this.instance.interceptors.response.use(
      this.interceptorsObj?.responseInterceptors,
      this.interceptorsObj?.responseInterceptorsCatch,
    )

    // --------------------------------------------------------------------------------
    // 4. 全局响应拦截器
    // --------------------------------------------------------------------------------
    this.instance.interceptors.response.use(
      (res: AxiosResponse) => {
        // 直接返回实际数据
        return res.data
      },
      (err: any) => {
        const { response } = err
        if (response) {
          // 处理标准 HTTP 错误
          toast.error(showMessage(response.status))
          return Promise.reject(response.data)
        }
        // 处理网络错误 (如超时、断网)
        // toast.warning('网络连接失败，请稍后重试')
        return Promise.reject(err)
      },
    )
  }

  /**
   * 通用请求方法
   * @param config 请求配置
   * @returns Promise<T>
   */
  request<T>(config: RequestConfig<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      // 如果定义了单个请求的拦截器，则执行
      if (config.interceptors?.requestInterceptors) {
        // 注意：强制转换为 any 以避免与 InternalAxiosRequestConfig 的严格类型不匹配
        config = config.interceptors.requestInterceptors(config as any) as any
      }

      this.instance
        .request<any, T>(config)
        .then((res) => {
          // 如果定义了单个请求的响应拦截器，则执行
          if (config.interceptors?.responseInterceptors) {
            res = config.interceptors.responseInterceptors(res as any)
          }
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  }
}

export default Request
